/* eslint-disable no-nested-ternary */
import React, { Component } from 'react';
import { Loader } from 'semantic-ui-react';
import map from 'lodash/map';
import find from 'lodash/find';
import sortBy from 'lodash/sortBy';
import capitalize from 'lodash/capitalize';

import { toast } from 'react-toastify';
import {
  getAdPrototypes,
  removeAdPrototype,
  updateAdPrototype
} from '../../../services/AdPrototypeService';
import { makeGetWithParam } from '../../../services/ServiceManager';
import AdPrototypeList from './AdPrototypeList';
import AdBuilderSelector from './AdBuilderSelector';
import AdBuilderBase from './AdBuilderBase';
import AdLibraryLaunchContainer from './AdLibraryLaunchContainer';
import AdBuilderAdPreviewModal from './AdBuilderAdPreviewModal';
import { getFormattedTags } from '../../../services/TagService';
import { getAccounts } from '../../../services/AccountService';
import { getCanvases } from '../../../services/CanvasService';
import { getProductCatalogs } from '../../../fbApi/ProductCatalog';
import domains from '../../../containers/Domains';
import './AdLibrary.css';

const { featureFlags } = domains || {};
const { adLibrary: adLibraryFlags } = featureFlags || {};
const { multiAdLaunchEnabled } = adLibraryFlags || {};
const initialQueryValue = '?activePage=1&rowsPerPage=4';

class AdLibrary extends Component {
  state = {
    section: 'search',
    adPrototypes: null,
    adPrototypesTags: [],
    productCatalogs: [],
    adPrototypesCards: [],
    loading: false,
    adType: '',
    accountOptions: [],
    formattedTags: {},
    canvases: [],
    totalPagesForInitialAdPrototypeList: 5,
    adPrototypeEdit: {},
    showAdBuilderAdPreviewModal: false,
    pageId: '1067945359908634', // constellation agency FB page ID
    adPrototypesToLaunch: [],
    outputQueryObject: {},
    mostRecentQuery: initialQueryValue,
    lastUrlUsed: '',
    qcWasProcessed: false
  };

  async componentDidMount() {
    this.props.setSectionTitle('Ad Library');
    this.props.saveGTMEvent('Ad Library', 'Alexia');
    this.setLoadingState(true);
    await this.fillTags();
    await this.getAdPrototypes();
    await this.getCanvases();
    await this.getAccounts();
    await this.fetchProductCatalogs();
    this.setLoadingState(false);
  }

  setLoadingState = (bool) => {
    this.setState({ loading: bool });
  };

  fillTags = async () => {
    const { groups, email } = this.props.user;
    const applyGuestFilter = ['NU'].includes(groups[0]);

    const result = await getFormattedTags(applyGuestFilter ? email : null);

    if (!result || result.statusCode !== 200 || !result.tags) {
      return 'failure';
    }

    this.setState({ formattedTags: result.tags });
    return 'success';
  };

  fetchProductCatalogs = async () => {
    const { getFbToken, user } = this.props;

    const result = await getProductCatalogs(
      getFbToken(),
      undefined,
      user.groups[0] // users only have one group in this app
    );

    const catalogs = result ? result.data : [];
    if (!catalogs) {
      toast.error('Product catalogs could not be pulled.', {
        position: 'top-center',
        autoClose: 4000,
        hideProgressBar: true,
        closeOnClick: true,
        pauseOnHover: true
      });
      return;
    }
    const catalogSelectionList = catalogs.map((catalog, index) => ({
      key: index,
      text: catalog.name,
      value: catalog.id
    }));

    this.setState({
      productCatalogs: catalogSelectionList
    });
  };

  getAccounts = async () => {
    try {
      const result = await getAccounts(this.props.user.groups[0]);

      const accountOptions = sortBy(
        map(result, (op, index) => ({
          key: index,
          value: op.account_id,
          text: op.name
        })),
        'text'
      );

      this.setState({
        accountOptions
      });
    } catch (error) {
      console.log('Something went wrong in getAccounts(): ', error);
    }
  };

  sortAdPrototypes = (adPrototypes) => {
    const sorted = adPrototypes.sort(function(a, b) {
      return b.id - a.id;
    });

    return sorted;
  };

  returnQueryStringAndObjectFromUrlParams = () => {
    let queryString = '';
    let queryObject = {};
    try {
      const urlParamsObject = this.returnUrlParamsObject();
      const {
        filterQuery,
        outputQueryObject
      } = this.convertUrlParamsObjectToQuery(urlParamsObject);
      queryString = filterQuery;
      queryObject = outputQueryObject;
    } catch (error) {
      const errorMessage = 'Something went wrong while reading the url';
      console.log(`${errorMessage}:`, error);
      toast.warn(errorMessage, {
        position: 'top-center',
        autoClose: 3500,
        hideProgressBar: true,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true
      });
    }
    return { queryString, queryObject };
  };

  returnUrlParamsObject = () => {
    const url = window.location.href;
    const decodedUrl = decodeURIComponent(url);
    const urlParts = decodedUrl.split('#');
    const urlParamsObject = {
      name: '',
      oem: '',
      type: '',
      year: '',
      product: '',
      campaign: '',
      month: '',
      package: '',
      page: 1
    };
    // to do: reafactor and handle page prop
    if (urlParts[1]) {
      const paramKeys = Object.keys(urlParamsObject);
      const params = urlParts[1].split('&');
      paramKeys.forEach((key) => {
        const stringAtIndex = find(
          params,
          (param) => param && param.indexOf(`${key}=`) !== -1
        );
        const splitString = stringAtIndex ? stringAtIndex.split('=') : '';
        const tagValues = splitString[1] ? splitString[1].split(',') : [];
        if (key === 'page') {
          urlParamsObject.page = splitString[1] || 1;
          return;
        }
        if (key === 'name') {
          urlParamsObject.name = tagValues.length > 0 ? tagValues[0] : '';
        } else {
          const tagIds = tagValues
            ? tagValues.map((tagValue) => {
                let trueTagValue = '';
                if (key !== 'type') {
                  trueTagValue = this.getTagIdFromValue(key, tagValue) || '';
                } else {
                  const lowerCaseValue = tagValue.toLowerCase();
                  trueTagValue =
                    lowerCaseValue !== 'daa'
                      ? capitalize(tagValue)
                      : lowerCaseValue.toUpperCase();
                }
                return trueTagValue;
              })
            : [];
          urlParamsObject[`${key}`] = tagIds.length > 0 ? tagIds.join(',') : '';
        }
      });
    }
    return urlParamsObject;
  };

  convertUrlParamsObjectToQuery = (urlParamsObject) => {
    const outputQueryObject = {};
    let filterQuery = '?';

    for (const key in urlParamsObject) {
      if (key === 'page') {
        continue;
      }
      const currentParam = urlParamsObject[key];
      if (!currentParam || currentParam.trim() === '') {
        continue;
      }
      if (key === 'name') {
        const queryParam = `name=${currentParam}&`;
        outputQueryObject.name = currentParam;
        filterQuery = filterQuery.concat(queryParam);
        continue;
      }
      const paramValues = currentParam.split(',');
      outputQueryObject[`${key}`] = paramValues;
      for (let index = 0; index < paramValues.length; index += 1) {
        const queryParam = `${key}[${index}]=${paramValues[index]}&`;
        filterQuery = filterQuery.concat(queryParam);
      }
    }

    filterQuery = filterQuery.concat(
      `activePage=${urlParamsObject.page || 1}&rowsPerPage=4`
    );

    return {
      filterQuery,
      outputQueryObject
    };
  };

  getTagIdFromValue = (key, tagValue) => {
    const { formattedTags } = this.state;
    const foundTag = formattedTags[key]
      ? formattedTags[key].find((tag) => tag.value === tagValue)
      : {};

    const { id: tagId } = foundTag;
    return tagId || null;
  };

  getAdPrototypes = async (inputQuery = null, inputQueryObject = null) => {
    let query = '';
    let outputQueryObject = {};
    const { groups, email } = this.props.user;

    try {
      const {
        queryString,
        queryObject
      } = this.returnQueryStringAndObjectFromUrlParams();
      query = inputQuery && inputQuery.trim() !== '' ? inputQuery : queryString;
      outputQueryObject = inputQueryObject || queryObject;
      let adPrototypesData;

      // This leaves room for possibility for future guests
      const applyGuestFilter = ['NU'].includes(groups[0]);

      if (applyGuestFilter) {
        const queryExt = `created_by=${encodeURIComponent(email)}`;
        query = query.trim() === '' ? `?${queryExt}` : `${query}&${queryExt}`;
      }

      if (query.trim() !== '') {
        adPrototypesData = await makeGetWithParam(
          'service/adprototype/adprototypes/filter',
          null,
          query
        );
      } else {
        adPrototypesData = await getAdPrototypes();
      }

      const { result } = adPrototypesData || {};

      if (!result) {
        throw new Error('Could not get Ad Prototypes. Refresh and try again.');
      }

      const {
        adPrototypes: resultAdPrototypes,
        tags: resultTags,
        cards: resultCards,
        totalPages
      } = result;

      // a timestamp row needs to be added to the db in order to use timestamps
      // const sortedAdPrototypes = resultAdPrototypes.sort((a, b) => {
      //   return Date.parse(b.timestamp) - Date.parse(a.timestamp);
      // });
      // however, we do not need timestamps because the id of the ad prototype autoincrements,
      // which means that the greater the id int value is, the more recent the prototype was created.
      // const finalAdPrototypes = sortedAdPrototypes.slice(0, 8);
      const sortedCards =
        resultCards instanceof Array ? sortBy([...resultCards], 'id') : [];
      const finalAdPrototypes = resultAdPrototypes || [];
      this.setState(
        (prevState) => ({
          ...prevState,
          adPrototypes: finalAdPrototypes,
          adPrototypesTags: resultTags || [],
          adPrototypesCards: sortedCards,
          totalPagesForInitialAdPrototypeList: totalPages || 0,
          outputQueryObject,
          lastUrlUsed: !window.location.href.split('#')[1]
            ? prevState.lastUrlUsed
            : window.location.href,
          mostRecentQuery:
            query !== initialQueryValue ? query : prevState.mostRecentQuery
        }),
        () => {
          if (!inputQuery || !outputQueryObject) {
            return;
          }
          // this helps restore the filter-triggering url after going back from create
          window.location.replace(this.state.lastUrlUsed);
        }
      );

      return (
        result || {
          adPrototypes: [],
          tags: [],
          cards: [],
          totalPages: 0,
          activePage: 1
        }
      );
    } catch (error) {
      console.log('Error occurred in getAdPrototypes():', error);
      this.setState((prevState) => ({
        ...prevState,
        adPrototypes: [],
        adPrototypesTags: [],
        adPrototypesCards: [],
        totalPagesForInitialAdPrototypeList: 0,
        outputQueryObject
      }));
      return {
        adPrototypes: [],
        tags: [],
        cards: [],
        totalPages: 0,
        activePage: 1
      };
    }
  };

  getCanvases = async () => {
    const { groups, email } = this.props.user;

    // This leaves room for possibility for future guests
    const applyGuestFilter = ['NU'].includes(groups[0]);

    const canvasesRes = await getCanvases(applyGuestFilter ? email : null);

    const canvasesOptions = sortBy(
      map(canvasesRes.canvases, (op, index) => ({
        key: index,
        value: op.id,
        text: op.name
      })),
      'text'
    );
    this.setState({ canvases: canvasesOptions });
  };

  searchPage = async () => {
    const { mostRecentQuery, outputQueryObject } = this.state;
    await this.fillTags();
    await this.getAdPrototypes(mostRecentQuery, outputQueryObject);
    this.setState((prevState) => ({
      ...prevState,
      section: 'search',
      adPrototypeEdit: '',
      adType: ''
    }));
  };

  adTypeSelector = () => {
    this.setState({
      section: 'adTypeSelector',
      adPrototypeEdit: '',
      adType: ''
    });
  };

  createAd = (adType) => {
    this.setState({ section: 'newAd', adPrototypeEdit: '', adType });
  };

  deleteAdPrototype = async (id) => {
    const result = await removeAdPrototype(id);
    const { statusCode } = result;
    await this.getAdPrototypes();
    if (statusCode && statusCode === 200) {
      toast.info(`Ad prototype deleted`, {
        position: 'bottom-left',
        autoClose: 3500,
        hideProgressBar: true,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true
      });
    } else {
      toast.error(
        `The operation to delete the ad prototype failed. Refresh and try again.`,
        {
          position: 'bottom-left',
          autoClose: 3500,
          hideProgressBar: true,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true
        }
      );
    }
    return Promise.resolve({ statusCode });
  };

  editAdPrototype = (adPrototype) => {
    const adPrototypeToUse = { ...adPrototype };
    const { adPrototypesCards } = this.state;
    const { cards } = adPrototypeToUse;
    if (!cards) {
      const filteredCards =
        adPrototypesCards instanceof Array
          ? sortBy(
              adPrototypesCards.filter(
                (card) => card.ad_prototype_id === adPrototype.id
              ),
              'id'
            )
          : [];
      adPrototypeToUse.cards = filteredCards;
    }
    this.setState({
      section: 'newAd',
      adPrototypeEdit: adPrototypeToUse,
      adType: adPrototype.type
    });
  };

  launchAdPrototype = (adPrototype) => {
    const adPrototypeToUse = { ...adPrototype };
    const { adPrototypesCards } = this.state;
    const { cards } = adPrototypeToUse;
    if (!cards) {
      const filteredCards = adPrototypesCards
        ? adPrototypesCards.filter(
            (card) => card.ad_prototype_id === adPrototype.id
          )
        : [];
      adPrototypeToUse.cards = filteredCards;
    }
    this.setState({
      section: 'launchAd',
      adPrototypeEdit: adPrototypeToUse,
      adType: adPrototype.type
    });
  };

  toggleAdBuilderAdPreviewModal = (adPrototype) => {
    const { showAdBuilderAdPreviewModal } = this.state;
    this.setState((prevState) => ({
      ...prevState,
      adPrototypeEdit: adPrototype || {},
      adType: adPrototype ? adPrototype.type : '',
      showAdBuilderAdPreviewModal: !showAdBuilderAdPreviewModal
    }));
  };

  launchAdsInQueue = (adLaunchQueue) => {
    const adLaunchQueueToUse = adLaunchQueue;

    this.setState((prevState) => ({
      ...prevState,
      section: 'launchAd',
      adPrototypesToLaunch: adLaunchQueueToUse
    }));
  };

  updateAdPrototype = async (adPrototypeData = {}) => {
    const { adPrototypes, adPrototypesTags } = this.state;
    const {
      id,
      qc_approved: qcApproved,
      footerCopy,
      footer_text: footerText,
      type
    } = adPrototypeData || {};

    this.setState((prevState) => ({ ...prevState, qcWasProcessed: false }));
    const adPrototypeIndex = adPrototypes.findIndex(
      (adPrototype) => adPrototype.id === id
    );
    if (adPrototypeIndex !== -1) {
      const tags = adPrototypesTags
        .filter((tag) => tag.ad_prototype_id === id)
        .map((tag) => tag.id);

      const newFooterCopy = footerCopy || footerText || '';

      const adPrototypeArgWithTags = {
        ...adPrototypeData,
        tags,
        footerCopy: newFooterCopy
      };

      delete adPrototypeArgWithTags.footer_text;

      if (type === 'DAA') {
        adPrototypeArgWithTags.thumbnail = '';
        delete adPrototypeArgWithTags.timestamp;
      }

      const updateResult = await updateAdPrototype(adPrototypeArgWithTags);
      const { statusCode, result } = updateResult;
      const { adPrototypeId } = result || {};

      let actionText = qcApproved === null && 'qc cleared';
      let toastType = qcApproved === null && 'default';

      if (typeof qcApproved === 'boolean') {
        actionText = qcApproved ? 'approved' : 'denied';
        toastType = qcApproved ? 'success' : 'error';
      }

      if (statusCode === 200 && adPrototypeId) {
        const newAdPrototypes = [...adPrototypes];
        newAdPrototypes[adPrototypeIndex] = adPrototypeData;
        toast(`Ad Preview ${actionText}`, {
          position: 'top-right',
          autoClose: 4000,
          hideProgressBar: true,
          closeOnClick: true,
          pauseOnHover: true,
          type: toastType
        });
        this.setState((prevState) => ({ ...prevState, qcWasProcessed: true }));
      } else {
        toast('Ad Preview QC could not be processed', {
          position: 'top-right',
          autoClose: 4000,
          hideProgressBar: true,
          closeOnClick: true,
          pauseOnHover: true,
          type: 'warning'
        });
      }
      return Promise.resolve({ statusCode, adPrototypeId });
    }
    toast('Could not get ad prototype qc data', {
      position: 'top-right',
      autoClose: 4000,
      hideProgressBar: true,
      closeOnClick: true,
      pauseOnHover: true,
      type: 'warning'
    });
    return Promise.resolve({});
  };

  render() {
    const {
      adPrototypes,
      adPrototypesTags,
      section,
      loading,
      adType,
      formattedTags,
      accountOptions,
      canvases,
      adPrototypeEdit,
      productCatalogs,
      adPrototypesCards,
      totalPagesForInitialAdPrototypeList,
      showAdBuilderAdPreviewModal,
      pageId,
      adPrototypesToLaunch,
      outputQueryObject,
      qcWasProcessed
    } = this.state;
    const { getFbToken, user } = this.props;
    const token = getFbToken();
    return (
      <div className="container">
        {section === 'search' ? (
          loading ? (
            <div className="ad-library-loading">
              <span>
                <Loader inline className="active" />
                Loading...
              </span>
            </div>
          ) : (
            <div>
              <AdBuilderAdPreviewModal
                showAdBuilderAdPreviewModal={showAdBuilderAdPreviewModal}
                toggleAdBuilderAdPreviewModal={
                  this.toggleAdBuilderAdPreviewModal
                }
                adPrototypeEdit={adPrototypeEdit}
                adType={adType}
                pageId={pageId}
                getFbToken={getFbToken}
                updateAdPrototypeForQc={this.updateAdPrototype}
                qcWasProcessed={qcWasProcessed}
              />
              <AdPrototypeList
                user={user}
                adPrototypes={adPrototypes}
                adPrototypesTags={adPrototypesTags}
                adTypeSelector={this.adTypeSelector}
                editAdPrototype={this.editAdPrototype}
                launchAdPrototype={this.launchAdPrototype}
                deleteAdPrototype={this.deleteAdPrototype}
                getAdPrototypes={this.getAdPrototypes}
                toggleAdBuilderAdPreviewModal={
                  this.toggleAdBuilderAdPreviewModal
                }
                adPrototypesCards={adPrototypesCards}
                tags={formattedTags}
                totalPages={totalPagesForInitialAdPrototypeList}
                fbToken={token}
                multiAdLaunchEnabled={multiAdLaunchEnabled}
                launchAdsInQueue={this.launchAdsInQueue}
                accountOptions={accountOptions}
                initialOutputQueryObject={outputQueryObject}
                updateAdPrototype={this.updateAdPrototype}
                qcWasProcessed={qcWasProcessed}
                fillTags={this.fillTags}
              />
            </div>
          )
        ) : null}
        {section === 'adTypeSelector' ? (
          <AdBuilderSelector
            searchPage={this.searchPage}
            createAd={this.createAd}
          />
        ) : null}
        {section === 'newAd' ? (
          <AdBuilderBase
            user={user}
            searchPage={this.searchPage}
            adType={adType}
            tags={formattedTags}
            accounts={accountOptions}
            getFbToken={getFbToken}
            canvases={canvases}
            adPrototypeEdit={adPrototypeEdit}
            updateAdPrototype={this.updateAdPrototype}
          />
        ) : null}
        {section === 'launchAd' ? (
          <AdLibraryLaunchContainer
            user={user}
            searchPage={this.searchPage}
            adType={adType}
            adPrototypesTags={adPrototypesTags}
            accountOptions={accountOptions}
            getFbToken={getFbToken}
            canvases={canvases}
            adPrototypesToLaunch={
              multiAdLaunchEnabled ? adPrototypesToLaunch : [adPrototypeEdit]
            }
            productCatalogs={productCatalogs}
          />
        ) : null}
      </div>
    );
  }
}

export default AdLibrary;



// WEBPACK FOOTER //
// ./src/screens/PackageBuilder/AdLibrary/AdLibrary.js